import { InputNumber, Modal, Select, message } from "antd";
import React, { useEffect, useState } from "react";
import {
  useCreateOutputPrice,
  useGetOutputPriceById,
  useUpdateOutputPrice,
} from "../../api/admin";
import { z } from "zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { WarehouseSelect } from "../warehouse-select";
import { ProductSelect } from "../product-select";

const OutputPriceSchema = z.object({
  selectedOutputId: z.string().min(1, { message: "Product is required" }),
  selectedWarehouseId: z.string().min(1, { message: "Warehouse is required" }),
  selectedYearId: z.string().min(1, { message: "Year is required" }),
  selectedPriceCycleId: z
    .string()
    .min(1, { message: "Price Cycle is required" }),
  contractPrice: z.preprocess(
    (val) =>
      val === undefined || val === null || val === "" ? undefined : Number(val),
    z.number().refine((val) => val > 0, {
      message: "Contract Price must be greater than 0",
    }),
  ),
  spotPrice: z.preprocess(
    (val) =>
      val === undefined || val === null || val === "" ? undefined : Number(val),
    z.number().refine((val) => val > 0, {
      message: "Spot Price must be greater than 0",
    }),
  ),
});

type OutputPriceForm = z.infer<typeof OutputPriceSchema>;

type OutputPriceModalProps = {
  open: boolean;
  selectedId?: number;
  hideModal: () => void;
};

const OutputPriceModal: React.FC<OutputPriceModalProps> = ({
  open,
  selectedId,
  hideModal,
}) => {
  const { data: selectedOutputPrice } = useGetOutputPriceById(selectedId);
  const { mutateAsync: createOutputPrice, isPending: creating } =
    useCreateOutputPrice();
  const { mutateAsync: updateOutputPrice, isPending: updating } =
    useUpdateOutputPrice();

  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState<number[]>([]);

  useEffect(() => {
    const _years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      _years.push(y);
    }
    setYears(_years);
  }, [currentYear]);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<OutputPriceForm>({
    resolver: zodResolver(OutputPriceSchema) as any,
    defaultValues: {
      selectedOutputId: "",
      selectedWarehouseId: "",
      selectedYearId: "",
      selectedPriceCycleId: "",
      contractPrice: undefined,
      spotPrice: undefined,
    },
  });

  useEffect(() => {
    if (selectedOutputPrice?.data?.data) {
      const data = selectedOutputPrice.data.data;
      setValue("selectedOutputId", String(data.outPut.id));
      setValue("selectedWarehouseId", String(data.warehouse.id));
      setValue("selectedYearId", String(data.year));
      setValue("selectedPriceCycleId", String(data.priceCycle));
      setValue("contractPrice", data.contractPrice);
      setValue("spotPrice", data.spotPrice);
    }
  }, [selectedOutputPrice, setValue]);

  const onSubmit: SubmitHandler<OutputPriceForm> = async (formData) => {
    try {
      if (selectedId === undefined) {
        const payload = {
          outPutId: Number(formData.selectedOutputId),
          warehouseId: Number(formData.selectedWarehouseId),
          year: Number(formData.selectedYearId),
          priceCycle: Number(formData.selectedPriceCycleId),
          contractPrice: formData.contractPrice!,
          spotPrice: formData.spotPrice!,
        };

        const result = await createOutputPrice(payload);
        if (result?.status === 200) {
          message.success("Price created successfully");
        }
      } else {
        const payload = {
          id: selectedId,
          price: {
            outPutId: Number(formData.selectedOutputId),
            warehouseId: Number(formData.selectedWarehouseId),
            year: Number(formData.selectedYearId),
            priceCycle: Number(formData.selectedPriceCycleId),
            contractPrice: formData.contractPrice!,
            spotPrice: formData.spotPrice!,
          },
        };
        const result = await updateOutputPrice(payload);
        if (result?.data?.data?.id) {
          message.success("Price updated successfully");
        }
      }
      hideModal();
      reset();
    } catch {
      message.error("Operation failed");
    }
  };

  const resetForm = () => reset();

  return (
    <Modal
      afterClose={resetForm}
      destroyOnClose
      title={
        <div className="flex flex-col items-center font-semibold text-[27.43px] text-black">
          {selectedId !== undefined
            ? "Edit Output Product Price"
            : "Create Output Product Price"}
        </div>
      }
      open={open}
      centered
      onOk={handleSubmit(onSubmit)}
      okButtonProps={{
        disabled: creating || updating,
        loading: creating || updating,
      }}
      onCancel={hideModal}
      closable
    >
      <div className="flex flex-col gap-4">
        {/* Product */}
        <div>
          {/* Product Select */}
          <Controller
            name="selectedOutputId"
            control={control}
            render={({ field }) => (
              <ProductSelect
                value={field.value || undefined}
                // onChange={(val) => field.onChange(val)}
                onChange={(val, option) => field.onChange(val, option)}
                status={errors.selectedOutputId ? "error" : undefined}
              />
            )}
          />
          {errors.selectedOutputId && (
            <span style={{ color: "red" }}>
              {errors.selectedOutputId.message}
            </span>
          )}
        </div>

        {/* Warehouse */}
        <div>
          <Controller
            name="selectedWarehouseId"
            control={control}
            render={({ field }) => (
              <WarehouseSelect
                value={field.value || undefined}
                onChange={(val, option) => field.onChange(val, option)}
                status={errors.selectedWarehouseId ? "error" : undefined}
              />
            )}
          />
          {errors.selectedWarehouseId && (
            <p className="text-red-500 text-sm">
              {errors.selectedWarehouseId.message}
            </p>
          )}
        </div>

        {/* Year */}
        <div>
          <Controller
            name="selectedYearId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Select Year"
                style={{ width: "100%" }}
                value={field.value || undefined}
                onChange={(val) => field.onChange(val)}
                options={years.map((y) => ({ label: y, value: String(y) }))}
                status={errors.selectedYearId ? "error" : undefined}
              />
            )}
          />
          {errors.selectedYearId && (
            <p className="text-red-500 text-sm">
              {errors.selectedYearId.message}
            </p>
          )}
        </div>

        {/* Price Cycle */}
        <div>
          <Controller
            name="selectedPriceCycleId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Select Price Cycle"
                style={{ width: "100%" }}
                value={field.value || undefined}
                onChange={(val) => field.onChange(val)}
                options={[
                  { value: "1", label: "JANUARY-MARCH" },
                  { value: "2", label: "APRIL-JUNE" },
                  { value: "3", label: "JULY-SEPTEMBER" },
                  { value: "4", label: "OCTOBER-DECEMBER" },
                ]}
                status={errors.selectedPriceCycleId ? "error" : undefined}
              />
            )}
          />
          {errors.selectedPriceCycleId && (
            <p className="text-red-500 text-sm">
              {errors.selectedPriceCycleId.message}
            </p>
          )}
        </div>

        {/* Contract Price */}
        <div>
          <Controller
            name="contractPrice"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                className="w-full"
                placeholder="Insert contract price"
                value={field.value}
                onChange={(val) => field.onChange(val)}
                status={errors.contractPrice ? "error" : undefined}
              />
            )}
          />
          {errors.contractPrice && (
            <p className="text-red-500 text-sm">
              {errors.contractPrice.message}
            </p>
          )}
        </div>

        {/* Spot Price */}
        <div>
          <Controller
            name="spotPrice"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                className="w-full"
                placeholder="Insert spot price"
                value={field.value}
                onChange={(val) => field.onChange(val)}
                status={errors.spotPrice ? "error" : undefined}
              />
            )}
          />
          {errors.spotPrice && (
            <p className="text-red-500 text-sm">{errors.spotPrice.message}</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default OutputPriceModal;
